import { Component, OnInit, OnDestroy, ViewChild, Injector, forwardRef, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { ControlValueAccessor, NgControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Subject } from 'rxjs';
import { ItensService } from '../../services/itens.service';
import { UnidadesEmpresaParametrosNfceService } from '../../services/unidades-empresa-parametros-nfce.service';
import { mergeArray, controlRequired, getErrorMessage } from '../../general/utils';
import { debounceTime, tap, switchMap, finalize } from 'rxjs/operators';
import { StorageFunctions } from '@generals';
import { UnidadeEmpresaParametrosServiceV2 } from '@sv2/unidade-empresa-parametros/unidade-empresa-parametros-v2.service';
import { UnidadeEmpresaParametrosEstoqueViewModel } from 'models/unidade-empresa-parametros/UnidadeEmpresaParametrosEstoqueViewModel';
import { MessageService } from '@services';
import { BalancasServiceV2, ItensServiceV2 } from '@servicesv2';
import { CadastroBalancasViewModel } from '../../venda/cadastros/balancas/model/CadastroBalancasViewModel';
import { ItensBipadosViewModel, ItensViewModel } from 'models/itens/ItensViewModel';

@Component({
  selector: 'app-combobox-produto',
  templateUrl: './combobox-produto.component.html',
  styleUrls: ['./combobox-produto.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ComboboxProdutoComponent),
      multi: true
    }
  ]
})
export class ComboboxProdutoComponent implements OnInit, ControlValueAccessor, OnDestroy {

  ngControl: NgControl;
  disabled = false;
  idProdutoSelecionado = null;
  private objetoProdutoSelecionado = null;

  private barcodeBuffer = '';
  private barcodeTimeout: any;
  private readonly BARCODE_TIMEOUT = 100;
  private readonly MIN_BARCODE_LENGTH = 11;
  parametrosEstoque: UnidadeEmpresaParametrosEstoqueViewModel = null;
  balancaPadrao: CadastroBalancasViewModel = null;

  @ViewChild('select', { static: false }) comboProdutos: NgSelectComponent;
  @Input() label = 'Produto';
  @Input() appendTo = 'body';
  @Input() filtraVendedor = false;
  @Input() exibeAlteraItem = false;
  @Input() exibeClonaItem = false;
  @Input() filtroItensFilhos: number = null;
  @Input() fornecedorCnpj = null;
  @Input() autofocus = false;
  @Input() startOpen = false;
  @Input() readonly = false;
  @Input() autoload = true; // Para uso em telas com muitos combobox-produto, só carregar dados quando o usuário interagir com o campo.
  @Input() mostrarFiltro = true; // Mostrar escolha de filtro por código, descrição, etc.
  @Input() placeholder = 'Selecione';
  @Input() habilitarLeitorOptico = true; // Habilitar detecção de leitura óptica

  selectOptions = {
    enable: true,
    multiple: true,
    lastValue: 0,
    options: [
      { attr: 'referenciaItem', value: 'Código', checked: false, format: '{referenciaItem} - {nome}' },
      { attr: 'nome', value: 'Descrição', checked: true, format: '{referenciaItem} - {nome}' },
      { attr: 'cEAN', value: 'EAN', checked: false, format: '{cEAN} - {nome}' },
      { attr: 'cEANTributavel', value: 'EAN Tributável', checked: false, format: '{cEANTributavel} - {nome}' },
      { attr: 'codigoEntrada', value: 'Código Entrada', checked: false, format: '{codigoEntrada} - {nome}' },
      { attr: 'descricaoComplementar', value: 'Descr. Complementar', checked: false, format: '{descricaoComplementar} - {nome}' },
    ]
  };

  produtos = [];
  produtoInput = new Subject<string>();
  loading: any = {};
  @Input() atalho = null;
  @Input() pesquisaSelecionada = "nome"; // Convertido de lista para string na WEB-1824, a pedido do Edson
  @Output() change = new EventEmitter();
  @Output() detalhaItem = new EventEmitter();
  @Output() clonaItem = new EventEmitter();
  @Output() carregouItemBipado = new EventEmitter();

  constructor(
    private itensService: ItensService,
    private itensServiceV2: ItensServiceV2,
    private parametrosNfceService: UnidadesEmpresaParametrosNfceService,
    private unidadeEmpresaParametrosServiceV2: UnidadeEmpresaParametrosServiceV2,
    private balancasServiceV2: BalancasServiceV2,
    private messageService: MessageService,
    private storage: StorageFunctions,
    private inj: Injector) { }

  ngOnInit() {
    this.ngControl = this.inj.get(NgControl);
    if (this.mostrarFiltro) {
      this.carregaParamConsulta();
    }
    this.onSearchProdutos();
    if (this.autoload) {
      this.fetchMoreProduto('');
    }
    if (this.autofocus) {
      this.setFocus();
    }
    if (this.startOpen) {
      this.openSearch();
    }

    var f = this.storage.getComboboxStorageFilters('ComboboxFiltroPesquisaProduto');
    if (f) {
      this.selectOptions.options = f;
    }

    if (this.habilitarLeitorOptico) {
      this.obterParametrosEstoque();
    }
  }

  setFocus() {
    setTimeout(() => {
      this.comboProdutos.focus();
    }, 100);
  }

  openSearch() {
    setTimeout(() => {
      this.comboProdutos.focus();
      this.comboProdutos.open();
    }, 100);
  }

  closeSearch() {
    setTimeout(() => {
      this.comboProdutos.blur();
      this.comboProdutos.close();
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.mostrarFiltro) {
      const checkedSelectOptions = this.retornaCheckedSelectOptions().map(x => x.attr);
      this.parametrosNfceService.putParametrosPesquisaProduto(checkedSelectOptions.join(';')).toPromise().catch(() => null);
    }

    if (this.barcodeTimeout) {
      clearTimeout(this.barcodeTimeout);
    }
  }

  writeValue(obj: any): void {
    this.idProdutoSelecionado = obj;
    this.carregaidProdutoSelecionado();
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private propagateChange = (_: any) => { };

  onTouched = () => { };

  onChange = (event) => {
    const newValue = event && event.id ? event.id : null;
    this.idProdutoSelecionado = newValue;
    this.carregaObjetoProdutoSelecionado(event);
    this.propagateChange(this.idProdutoSelecionado);
    this.change.emit(event);
    this.comboProdutos.close();
  };

  // Force pode usado para recarregar o produto quando foi editado o código/descrição e é necessário mostrar essa alteração no combobox.
  carregaidProdutoSelecionado(force = false) {
    if (this.idProdutoSelecionado && (!this.produtos.find(t => t.id === this.idProdutoSelecionado) || force)) {
      this.loading['produto'] = true;
      this.itensService.getItemByIdOData(this.idProdutoSelecionado)
        .pipe(finalize(() => this.loading['produto'] = false))
        .subscribe(res => {
          this.change.emit(res);
          if (res) {
            if (force) {
              this.produtos = this.produtos.filter(t => t.id !== this.idProdutoSelecionado);
            }
            this.produtos = mergeArray(this.produtos, [res]);
            this.carregaObjetoProdutoSelecionado(res.id);
          }
        });
    }
  }

  async carregaParamConsulta() {
    try {
      this.loading['paramConsulta'] = true;
      const res = await this.parametrosNfceService.getParametroPesquisaProduto().toPromise() as string;
      res.split(';').forEach(x => {
        this.selectOptions.options.find(y => y.attr == x).checked = true;
      })

      this.pesquisaSelecionada = res ? res[0] : 'nome';
    } catch (error) {
      console.error(error);
    } finally {
      this.loading['paramConsulta'] = false;
    }
  }

  fetchMoreProduto(term) {
    this.loading['produtos'] = true;

    this.itensService.getItensComboboxPedido(this.criaFormConsulta(term || ''))
      .pipe(finalize(() => this.loading['produtos'] = false))
      .subscribe(result => {
        this.produtos = mergeArray(this.produtos, result);
      });
  }

  onSearchProdutos() {
    this.produtoInput.pipe(
      debounceTime(500),
      tap(() => this.loading['produtos'] = true),
      switchMap(term => {
        return this.itensService.getItensComboboxPedido(this.criaFormConsulta(term || '', true)).pipe(
          tap(() => this.loading['produtos'] = false)
        )
      })
    )
      .pipe(finalize(() => this.loading['produtos'] = false))
      .subscribe(data => {
        this.produtos = data;

        if (this.produtos && this.produtos.length === 1) {
          this.onChange(this.produtos[0]);
        }
      }, () => {
        this.onSearchProdutos();
      });
  }

  criaFormConsulta(term, novaConsulta = false) {
    let pagina = 1;
    if (!novaConsulta) {
      pagina = Math.trunc(this.produtos.length / 10) + 1
    }

    return {
      searchTerm: term || '',
      filtraVendedor: this.filtraVendedor,
      Codigo: this.verificaFiltro('referenciaItem'),
      Descricao: this.verificaFiltro('nome'),
      EAN: this.verificaFiltro('cEAN'),
      EANTrib: this.verificaFiltro('cEANTributavel'),
      CodigoEntrada: this.verificaFiltro('codigoEntrada'),
      DescricaoComplementar: this.verificaFiltro('descricaoComplementar'),
      fornecedorCnpj: this.fornecedorCnpj,
      filtroItensFilhos: this.filtroItensFilhos,
      pagina: pagina
    }
  }

  verificaFiltro(nomeFiltro) {
    return this.selectOptions.options.filter(x => x.attr === nomeFiltro)[0].checked;
  }

  onClose() {
    if (!this.idProdutoSelecionado) {
      this.produtos = [];
      this.fetchMoreProduto('');
    }
    this.onTouched();
  }

  retornaCheckedSelectOptions() {
    return this.selectOptions.options.filter(x => x.checked);
  }

  mostraSelectOptions() {
    return this.selectOptions.enable && this.mostrarFiltro;
  }

  verificaSelectOptionMarcado(opcao, index) {
    if (!this.selectOptions.multiple && this.selectOptions.lastValue === index) {
      return true;
    }

    if (this.selectOptions.multiple && opcao.checked) {
      return true;
    }

    return false;
  }

  alteraSelectOptions(event, indexOption) {
    this.produtoInput.next('');

    this.selectOptions.lastValue = indexOption;

    if (this.selectOptions.multiple) {
      const checked = this.selectOptions.options[indexOption].checked;

      if (!(checked && this.retornaCheckedSelectOptions().length == 1)) {
        this.selectOptions.options[indexOption].checked = !checked;
      }
    } else {
      this.selectOptions.options.forEach((option, index) => {
        option.checked = index === indexOption;
      });
    }

    const selectedOptions = this.retornaCheckedSelectOptions();
    if (selectedOptions.length > 0) {
      this.pesquisaSelecionada = selectedOptions[0].attr;
    }

    this.produtos = [];
    this.fetchMoreProduto('');
    this.storage.setComboboxStorageFilters('ComboboxFiltroPesquisaProduto', this.selectOptions.options);
  }

  isParametroSelecionado(op: string): boolean {
    const option = this.selectOptions.options.find(x => x.attr === op);
    return option ? option.checked : false;
  }

  alteraParametroConsulta(param: string, index: number) {
    this.alteraSelectOptions(null, index);
  }

  get descricaoParametroConsulta() {
    const selectedOptions = this.retornaCheckedSelectOptions();
    if (selectedOptions.length === 1) {
      return selectedOptions[0].value;
    } else if (selectedOptions.length > 1) {
      return selectedOptions.map(x => x.value).join(', ');
    }
    return 'Descrição';
  }

  templateDescricaoItens(item): string {
    let ret: string[] = [];
    if (this.verificaFiltro('referenciaItem')) {
      ret.push(item['referenciaItem'] || 'Sem referência');
    }
    if (this.verificaFiltro('cEAN')) {
      ret.push(item['cEAN'] || 'Sem EAN');
    }
    if (this.verificaFiltro('cEANTributavel')) {
      ret.push(item['cEANTributavel'] || 'Sem EAN Trib.');
    }
    if (this.verificaFiltro('codigoEntrada') && item.itensFornecedores && item.itensFornecedores.length > 0) {
      ret.push(item.itensFornecedores[0]['referenciaItem'] || 'Sem referência');
    }

    ret.push(item['nome']);

    return ret.filter(n => n && n?.length >= 1).join(' - ');
  }

  isOnlyEanSelectedAndOneProduct() {
    return this.pesquisaSelecionada === 'cEAN' &&
      this.produtos && this.produtos.length === 1;
  }

  public get objetoSelecionado(): any {
    return this.objetoProdutoSelecionado;
  }

  private carregaObjetoProdutoSelecionado(value) {
    if (value) {
      this.objetoProdutoSelecionado = this.produtos.find(c => c.id === this.idProdutoSelecionado);
    } else {
      this.objetoProdutoSelecionado = null;
    }
  }

  isFieldRequired(): boolean {
    return controlRequired(this.ngControl.control);
  }

  cadastraEditaItem(event) {
    this.detalhaItem.emit(event);
  }

  clonarItem(event, item) {
    // Não selecionar o item na hora de clicar em clonar, para não mostrar erro de falta de estoque.
    event.preventDefault();
    event.stopPropagation();

    this.clonaItem.emit(item);
  }

  onClear() {
    this.produtoInput.next('');
  }

  focus() {
    setTimeout(() => {
      this.comboProdutos.focus();
    }, 100);
  }

  blur() {
    setTimeout(() => {
      this.comboProdutos.blur();
    }, 100);
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (!this.habilitarLeitorOptico) {
      return;
    }

    if (!this.isFocused() && !this.isInputFocused()) {
      return;
    }

    if (event.key === 'Enter' && this.barcodeBuffer.length >= this.MIN_BARCODE_LENGTH) {
      event.preventDefault();
      this.processarCodigoBarras(this.barcodeBuffer);
      this.limparBufferCodigoBarras();
      return;
    }

    if (this.isValidBarcodeChar(event.key)) {
      this.adicionarCaractereBuffer(event.key);
    }
  }

  private isFocused(): boolean {
    return this.comboProdutos && this.comboProdutos.focused;
  }

  private isInputFocused(): boolean {
    const activeElement = document.activeElement;
    return activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.classList.contains('ng-select-container')
    );
  }

  private isValidBarcodeChar(key: string): boolean {
    return /^[a-zA-Z0-9\-_.]$/.test(key);
  }

  private adicionarCaractereBuffer(char: string) {
    this.barcodeBuffer += char;

    if (this.barcodeTimeout) {
      clearTimeout(this.barcodeTimeout);
    }

    this.barcodeTimeout = setTimeout(() => {
      this.limparBufferCodigoBarras();
    }, this.BARCODE_TIMEOUT);
  }

  private limparBufferCodigoBarras() {
    this.barcodeBuffer = '';
    if (this.barcodeTimeout) {
      clearTimeout(this.barcodeTimeout);
      this.barcodeTimeout = null;
    }
  }

  private processarCodigoBarras(codigoDeBarras: string) {
    if (!this.codigoDigitadoValido(codigoDeBarras)) {
      return;
    }

    this.buscarProdutoPorCodigo(codigoDeBarras);
  }

  private codigoDigitadoValido(codigoDeBarras: string) {
    if (codigoDeBarras[0] !== "2") {
      // Consulta será realizada normalmente.
      return false;
    }

    if (!this.parametrosEstoque?.balancaPadraoID || !this.balancaPadrao) {
      this.messageService.error('Balança padrão não configurada. Item de balança não pode ser localizado');
      return false;
    }

    return true;
  }

  private async buscarProdutoPorCodigo(codigoDeBarras: string) {
    try {
      const codigoProduto = this.extrairCodigoBalanca(codigoDeBarras);
      const item = await this.itensServiceV2.consultaItemBalanca(codigoProduto);
      if (!item) {
        // msg de erro vem do back
        return;
      }

      this.onChange(item);
      this.produtos = mergeArray(this.produtos, [item]);
      const dadosProduto: ItensBipadosViewModel = this.extrairDadosBalanca(codigoDeBarras, item);
      this.carregouItemBipado.emit(dadosProduto);
    } catch (error) {
      this.messageService.error(getErrorMessage(error), 12000);
      console.error(error);
    }
  }

  private extrairCodigoBalanca(codigoDeBarras: string) {
    const inicioCodigo = 1;
    const fimCodigo = inicioCodigo + this.balancaPadrao.digitosCodigo;
    const codigoProduto = codigoDeBarras.slice(inicioCodigo, fimCodigo);
    return codigoProduto;
  }

  private extrairDadosBalanca(codigoBarras: string, item: ItensViewModel): ItensBipadosViewModel {
    const codigoProduto = this.extrairCodigoBalanca(codigoBarras);

    // --- INÍCIO DO BLOCO DE PESO/VALOR ---
    const inicioPesoValor = 1 + this.balancaPadrao.digitosCodigo + this.balancaPadrao.zerosFixos;
    const fimPesoValor = inicioPesoValor + this.balancaPadrao.digitosPesoValor;
    const parteNumerica = codigoBarras.slice(inicioPesoValor, fimPesoValor);

    let quantidade, precoUnitario, valorTotal;

    if (this.balancaPadrao.informacaoEtiqueta === "V") {
      // --- ETIQUETA DE VALOR ---
      const valor = parseFloat(
        parteNumerica.slice(0, -2) + "." + parteNumerica.slice(-2)
      );

      quantidade = 1;
      precoUnitario = valor;
      valorTotal = precoUnitario * quantidade;
    } else if (this.balancaPadrao.informacaoEtiqueta === "P") {
      // --- ETIQUETA DE PESO ---
      const peso = parseFloat(
        parteNumerica.slice(0, -3) + "." + parteNumerica.slice(-3)
      );

      quantidade = peso;
      precoUnitario = item.precoSaida;
      valorTotal = quantidade * precoUnitario;
    }

    return {
      codigoProduto,
      quantidade,
      precoUnitario,
      valorTotal,
    };
  }


  async obterParametrosEstoque() {
    try {
      const parametros = await this.unidadeEmpresaParametrosServiceV2.getParametrosEstoque();
      if (parametros) {
        this.parametrosEstoque = parametros;
        await this.obterBalancaPadrao();
      }
    } catch (error) {
      console.error(error);
    }
  }

  async obterBalancaPadrao() {
    try {
      this.balancaPadrao = await this.balancasServiceV2.getAsPromise<CadastroBalancasViewModel>(this.parametrosEstoque.balancaPadraoID?.toString());
    } catch (error) {
      console.error(error);
    }
  }
}
